import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WorkbookCard from "./WorkbookCard";
import FactorCard from "./FactorCard";
import WorkbookEmptyState from "./WorkbookEmptyState";
import {
  getWorkbookOverview,
  resolveWorkbookOrganizationSurveyId,
} from "../organizationWorkbookApi";
import { getWorkbookRoutePrefix, isReadOnlyWorkbookRoute, workbookPath } from "./workbookRouting";
import { formatOrganizationSurveyTitle } from "../../../utils/organizationSurveyHelper";

const isValidKind = (kind) => kind === "strength" || kind === "development";

const pickArray = (payload, keys) => {
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const pickItem = (payload, keys) => {
  for (const key of keys) {
    if (payload?.[key]) return payload[key];
  }
  return null;
};

const factorOrder = [
  { id: "input", labelKey: "organization.workbook.factors.input" },
  { id: "process", labelKey: "organization.workbook.factors.process" },
  {
    id: "culture_climate",
    labelKey: "organization.workbook.factors.culture_climate",
  },
];

const WorkbookOverviewPage = () => {
  const { t } = useTranslation();
  const { kind, organizationId } = useParams();
  const location = useLocation();
  const routePrefix = getWorkbookRoutePrefix(location.pathname);
  const readOnly = isReadOnlyWorkbookRoute(location.pathname, location.state);
  const [overview, setOverview] = useState(null);
  const [organizationSurveyId, setOrganizationSurveyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isValidKind(kind)) return;

    let active = true;

    const loadOverview = async () => {
      try {
        setLoading(true);
        setError("");
        const resolvedSurveyId = await resolveWorkbookOrganizationSurveyId({
          routeStateOrganizationSurveyId: location.state?.organizationSurveyId,
          organizationId,
        });
        const data = await getWorkbookOverview(resolvedSurveyId, kind);
        if (!active) return;
        setOrganizationSurveyId(resolvedSurveyId);
        setOverview(data);
      } catch (err) {
        if (!active) return;
        setError(t("organization.workbook.load_error", "Could not load the workbook."));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadOverview();

    return () => {
      active = false;
    };
  }, [kind, location.state?.organizationSurveyId, organizationId]);

  const factorCards = useMemo(() => {
    if (!overview) return [];

    return factorOrder.map((factor) => {
      const candidateKeys =
        factor.id === "culture_climate"
          ? ["culture_climate", "culture_and_climate", "climate_and_culture"]
          : [factor.id];
      const data = pickItem(overview, candidateKeys) || pickItem(overview?.factors, candidateKeys);

      return {
        id: factor.id,
        title: t(factor.labelKey),
        description: data?.description,
        result: data?.result,
      };
    });
  }, [overview, t]);

  if (!isValidKind(kind)) {
    return <Navigate to={workbookPath(routePrefix)} replace />;
  }

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const pages = pickArray(overview, ["pages", `${kind}_pages`]);
  const accentColor = kind === "strength" ? "green.500" : "orange.500";
  const surveyTitle = formatOrganizationSurveyTitle(overview || {});

  return (
    <VStack align="stretch" spacing={8}>
      <Box>
        <Heading size="lg">
          {kind === "strength"
            ? t("organization.workbook.strength_overview_title", "Strength Workbook")
            : t("organization.workbook.development_overview_title", "Development Workbook")}
        </Heading>
        {surveyTitle ? (
          <Text fontWeight="semibold" mt={2}>
            {surveyTitle}
          </Text>
        ) : null}
        <Text color="gray.600" mt={2} maxW="3xl">
          {kind === "strength"
            ? t(
                "organization.workbook.strength_overview_intro",
                "Start with the areas that are already working strongly, then review their interpretation and supporting practices."
              )
            : t(
                "organization.workbook.development_overview_intro",
                "Start with the areas that need the most attention, then move into interpretation and next day practices."
              )}
        </Text>
      </Box>

      <Box>
        <Heading size="md" mb={4}>
          {t("organization.workbook.factor_cards_title", "Focus factors")}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          {factorCards.map((factor) => (
            <FactorCard
              key={factor.id}
              title={factor.title}
              description={factor.description}
              result={factor.result}
              accentColor={accentColor}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <Heading size="md" mb={4}>
          {t("organization.workbook.selected_pages_title", "Selected pages")}
        </Heading>

        {pages.length === 0 ? (
          <WorkbookEmptyState
            title={t("organization.workbook.no_pages_title", "No workbook pages yet")}
            description={t(
              "organization.workbook.no_pages_description",
              "Selected workbook pages will appear here when this area is available."
            )}
          />
        ) : (
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
            {pages.map((page) => {
              const pageId = page.page_id || page.id;

              return (
                <WorkbookCard
                  key={pageId}
                  title={page.title}
                  areaLabel={page.area_label}
                  result={page.result}
                  description={page[kind] || page.content}
                  ctaLabel={t(
                    "organization.workbook.start_area",
                    "Start Working on This Area"
                  )}
                  ctaTo={workbookPath(routePrefix, `pages/${pageId}`)}
                  ctaState={{ organizationSurveyId, ...(readOnly ? { readOnly: true } : {}) }}
                  accentColor={accentColor}
                />
              );
            })}
          </SimpleGrid>
        )}
      </Box>
    </VStack>
  );
};

export default WorkbookOverviewPage;
